import { Flex, Text, Tag, Alert, Skeleton, Divider, Button } from '@nula/core-ui';
import type { SubscriptionStatus, CreditBalance } from './types';
import {
  formatTrialDaysLeft,
  formatRenewalDate,
  statusTagColor,
  formatBillingCadence,
  formatSavedCard,
  formatMoney,
} from './format';
import { hasSavedCard } from './plan-cta';
import { CreditBanner } from './credit-banner';
import { CreditMeter } from './credit-meter';
import styles from './billing-status-card.module.css';

interface BillingStatusCardProps {
  subscription?: SubscriptionStatus;
  credits?: CreditBalance;
  loading?: boolean;
  onSubscribe?: () => void;
  onChangePlan?: () => void;
  onManageBilling?: () => void;
  onCancel?: () => void;
  onResume?: () => void;
}

export function BillingStatusCard({
  subscription,
  credits,
  loading,
  onSubscribe,
  onChangePlan,
  onManageBilling,
  onCancel,
  onResume,
}: BillingStatusCardProps) {
  if (loading || !subscription) {
    return <Skeleton active data-testid="billing-status-skeleton" />;
  }

  const trialing = subscription.status === 'trialing';
  const daysLeft = trialing ? formatTrialDaysLeft(subscription.trialEndsAt) : 0;
  const isBasic = subscription.planCode === 'basic';
  const canSubscribe = subscription.isSelfServe && (isBasic || subscription.status === 'canceled');
  const showManage = !isBasic && subscription.status !== 'canceled';
  const cancelling = subscription.cancelAtPeriodEnd;
  // A trial with a card saved has no other route to change it: its plan is the
  // disabled "Current plan" marker in the catalog, and checkout would 409. The
  // portal accepts a trialing workspace — create_portal_session creates the
  // Stripe customer on demand for exactly this.
  const canUpdateCard = trialing && hasSavedCard(subscription);
  const savedCard = formatSavedCard(
    subscription.paymentMethodBrand,
    subscription.paymentMethodLast4,
  );
  const upcomingAmount = formatMoney(
    subscription.upcomingAmountCents,
    subscription.upcomingAmountCurrency,
  );

  return (
    <Flex vertical gap="md" className={styles.card}>
      <Flex className={styles.blob} />

      {trialing && (
        <Flex gap="sm" align="start" className={styles.trialBanner}>
          <span className={styles.trialDays}>{daysLeft}</span>
          <Text size="sm" className={styles.trialText}>
            <strong>
              {daysLeft} day{daysLeft === 1 ? '' : 's'} of {subscription.planName} left.
            </strong>{' '}
            {savedCard ? (
              <>
                Your trial ends {formatRenewalDate(subscription.trialEndsAt)} — {savedCard} will be
                charged{upcomingAmount === '—' ? '' : ` ${upcomingAmount}`} to continue on{' '}
                {subscription.planName}.
              </>
            ) : (
              <>
                Add a card before your trial ends to keep {subscription.planName}, or you&apos;ll
                move to Basic.
              </>
            )}
          </Text>
        </Flex>
      )}

      {credits && (
        <CreditBanner
          balance={credits.balance}
          monthlyAllowance={credits.monthlyAllowance}
          creditsResetAt={credits.creditsResetAt}
        />
      )}

      {cancelling && (
        <Alert
          type="warning"
          showIcon
          message="Cancelling"
          description={`Your plan ends on ${formatRenewalDate(
            subscription.currentPeriodEnd,
          )}. You'll keep access until then.`}
        />
      )}

      <Flex align="center" gap="sm">
        <Text size="md" className={styles.planName}>
          {subscription.planName}
        </Text>
        {!isBasic && (
          <Tag color={statusTagColor(subscription.status)}>{subscription.status}</Tag>
        )}
      </Flex>

      {!isBasic && (
        <Text size="sm" type="secondary">
          {formatBillingCadence(subscription.interval)}
        </Text>
      )}

      {/* The trial banner already names the card; repeating it here would say
          the same thing twice in the same card. */}
      {savedCard && !trialing && (
        <Text size="sm" type="secondary" className={styles.savedCard}>
          {savedCard}
        </Text>
      )}

      <Flex gap="sm" wrap className={styles.stats}>
        <div className={styles.statTile}>
          <Text size="xs" className={styles.statKey}>
            Credits
          </Text>
          {credits ? (
            <CreditMeter
              balance={credits.balance}
              monthlyAllowance={credits.monthlyAllowance}
              creditsResetAt={credits.creditsResetAt}
            />
          ) : (
            <Text className={styles.statValue}>0</Text>
          )}
        </div>

        {subscription.workspaceType === 'team' && (
          <div className={styles.statTile}>
            <Text size="xs" className={styles.statKey}>
              Seats
            </Text>
            <Text className={styles.statValue}>{subscription.seatCount}</Text>
          </div>
        )}

        {!isBasic && (
          <div className={styles.statTile}>
            <Text size="xs" className={styles.statKey}>
              Renews
            </Text>
            <Text className={styles.statValue}>{formatRenewalDate(subscription.currentPeriodEnd)}</Text>
          </div>
        )}
      </Flex>

      <Divider />

      <Flex gap="sm" wrap>
        {canSubscribe && (
          <Button variant="primary" size="sm" pressEffect="glow" onClick={onSubscribe}>
            Upgrade to Pro
          </Button>
        )}
        {showManage && onChangePlan && (
          <Button variant="primary" size="sm" pressEffect="glow" onClick={onChangePlan}>
            Change plan
          </Button>
        )}
        {canUpdateCard && (
          <Button variant="default" size="sm" onClick={onManageBilling}>
            Update card
          </Button>
        )}
        {showManage && (
          <Button variant={canUpdateCard ? 'text' : 'default'} size="sm" onClick={onManageBilling}>
            Manage billing
          </Button>
        )}
        {showManage && cancelling && onResume && (
          <Button variant="default" size="sm" onClick={onResume}>
            Resume plan
          </Button>
        )}
        {showManage && !cancelling && onCancel && (
          <Button variant="text" size="sm" danger onClick={onCancel}>
            Cancel plan
          </Button>
        )}
      </Flex>
    </Flex>
  );
}
